/**
 * Экран 11 «Настройки компании → Сезон» (AK-434, wireframe v3
 * `docs/superpowers/prototypes/annual-planning/11-season-settings.html`).
 *
 * Три блока: имя сезона · сколько видно в списках · границы сезона — и плашка
 * готовности раздела. (Окно открытия планирования убрано по UAT 05.08, Р-5.) Кнопка
 * «Сохранить настройки» проставляет `configured_at` и ОТКРЫВАЕТ раздел
 * «Планирование сезона» (гейт SA-03 §2). Право на PUT — company_settings:U,
 * при 403 бэк вернёт «Недостаточно прав».
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
// CalendarClock — иконка блока пересчёта (AK-541b, ниже под «Готовностью раздела»).
// Building2 и api больше не нужны здесь — NoCompanyState/PickCompanyState
// вынесены в EmptyCompanyStates.tsx рефакторингом реворка #667.
import { CalendarCheck2, CalendarClock, Lock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Skeleton } from '@/components/ui/skeleton';
import { Stack } from '@/components/ui/stack';
import { NoCompanyState, PickCompanyState } from '@/pages/planning/EmptyCompanyStates';
import {
  classifySeasonConfigError,
  getSeasonConfig,
  saveSeasonConfig,
  type SeasonConfig,
  type SeasonConfigPatch,
} from '@/lib/planning/api';
import { useActiveSeason } from '@/lib/season/SeasonContext';

/** Радио «как называются сезоны» → пара (label_format, name_template). */
type NameChoice = 'year' | 'year_span' | 'custom';

function nameChoiceOf(cfg: SeasonConfig): NameChoice {
  if (cfg.name_template) return 'custom';
  return cfg.label_format === 'year_span' ? 'year_span' : 'year';
}

/** Числовое поле: пустая строка = null (снять ограничение). */
function numOrNull(v: string): number | null {
  const t = v.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

export function SeasonSettingsTab() {
  const qc = useQueryClient();
  // Окно видимости сезонов живёт и в шапке (SeasonPicker через
  // SeasonProvider, AK-790). После сохранения перечитываем сезон принудительно,
  // иначе новое «показывать N назад» доехало бы до шапки только после F5 —
  // по нашим принципам UX это усталостный класс багов.
  const { refetch: refetchSeason } = useActiveSeason();
  // companyId проставляется ТОЛЬКО после выбора в селекторе (ветка 409). Пока
  // null — бэк сам находит единственную компанию тенанта.
  const [companyId, setCompanyId] = useState<string | null>(null);

  const config = useQuery({
    queryKey: ['planning', 'season-config', companyId],
    queryFn: () => getSeasonConfig(companyId),
    // Штатные 4xx (нет компаний / выбор компании) не ретраим — это не сеть.
    retry: (failureCount, err) => {
      const kind = classifySeasonConfigError(err);
      if (kind !== 'other') return false;
      return failureCount < 1;
    },
  });

  // Локальный черновик формы; заполняется с сервера один раз на загрузку.
  const [draft, setDraft] = useState<SeasonConfigPatch | null>(null);
  const [nameChoice, setNameChoice] = useState<NameChoice>('year');
  useEffect(() => {
    if (!config.data || draft) return;
    const c = config.data;
    setNameChoice(nameChoiceOf(c));
    setDraft({
      boundary_type: c.boundary_type,
      custom_start_month: c.custom_start_month,
      custom_start_day: c.custom_start_day,
      label_format: c.label_format,
      name_template: c.name_template,
      visible_past_years: c.visible_past_years,
      visible_future_years: c.visible_future_years,
      planning_opens_offset_days: c.planning_opens_offset_days,
      planning_closes_offset_days: c.planning_closes_offset_days,
    });
  }, [config.data, draft]);

  const save = useMutation({
    mutationFn: (patch: SeasonConfigPatch) => saveSeasonConfig(patch, companyId),
    onSuccess: (fresh) => {
      qc.setQueryData(['planning', 'season-config', companyId], fresh);
      // Префиксная инвалидация — она же покроет старый ключ
      // ['planning', 'season-config'] на странице /planning (staleTime 60с
      // без неё оставил бы `is_configured: false` на смежном экране, и
      // founder после «Сохранить настройки → Перейти в Планирование сезона»
      // получал бы «Раздел закрыт»).
      qc.invalidateQueries({ queryKey: ['planning', 'season-config'] });
      qc.invalidateQueries({ queryKey: ['planning', 'season-cards'] });
      void refetchSeason();
    },
  });

  if (config.isLoading || (config.data && !draft)) {
    return (
      <Stack gap={4} className="mt-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </Stack>
    );
  }

  // Три штатных ветки бэка — три разных экрана вместо общего «Обновите
  // страницу». Классификация — по коду ответа, не по тексту (AK-541).
  if (config.isError) {
    const kind = classifySeasonConfigError(config.error);
    if (kind === 'no_company') {
      return <NoCompanyState hint="сможете настроить сезон" />;
    }
    if (kind === 'company_ambiguous') {
      return (
        <PickCompanyState
          onPick={(id) => setCompanyId(id)}
          title="Для какой компании настраиваем сезон?"
          hint="Настройка сезона у каждой своя — выберите, для какой сейчас работаем."
        />
      );
    }
    return (
      <p className="mt-4 text-sm text-destructive">
        Не удалось загрузить настройку сезона. Обновите страницу.
      </p>
    );
  }
  if (!config.data || !draft) {
    return (
      <p className="mt-4 text-sm text-destructive">
        Не удалось загрузить настройку сезона. Обновите страницу.
      </p>
    );
  }

  const cfg = config.data;
  const set = (patch: SeasonConfigPatch) => setDraft((d) => ({ ...d, ...patch }));

  const pickName = (choice: NameChoice) => {
    setNameChoice(choice);
    if (choice === 'year') set({ label_format: 'year', name_template: null });
    if (choice === 'year_span') set({ label_format: 'year_span', name_template: null });
    // custom: label_format остаётся, шаблон вводится в поле ниже
  };

  const submit = () => save.mutate(draft);

  return (
    <Stack gap={4} className="mt-4">
      <p className="text-sm text-muted-foreground">
        Как называются сезоны, сколько их видно и какими датами сезон ограничен.
        До первого сохранения раздел «Планирование сезона» закрыт.
      </p>

      {/* 1. Название */}
      <Card>
        <CardHeader className="p-4 pb-3">
          <CardTitle className="text-base">Как называются сезоны</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <Stack gap={3}>
            <RadioGroup value={nameChoice} onValueChange={(v) => pickName(v as NameChoice)}>
              <div className="flex min-h-10 items-center gap-3 py-2">
                <RadioGroupItem value="year" id="nm-year" />
                <Label htmlFor="nm-year" className="flex-1 text-sm font-normal">
                  <span className="font-medium">«Сезон 2027» — по году урожая.</span>{' '}
                  <span className="text-muted-foreground">
                    Осенний сев 2026 относится к сезону 2027.
                  </span>
                </Label>
              </div>
              <div className="flex min-h-10 items-center gap-3 py-2">
                <RadioGroupItem value="year_span" id="nm-span" />
                <Label htmlFor="nm-span" className="flex-1 text-sm font-normal">
                  <span className="font-medium">«2026–2027» — через два года.</span>{' '}
                  <span className="text-muted-foreground">
                    Видно, что сезон переходит из года в год.
                  </span>
                </Label>
              </div>
              <div className="flex min-h-10 items-center gap-3 py-2">
                <RadioGroupItem value="custom" id="nm-custom" />
                <Label htmlFor="nm-custom" className="flex-1 text-sm font-normal">
                  <span className="font-medium">Свой шаблон.</span>{' '}
                  <span className="text-muted-foreground">
                    Подстановки: {'{year}'}, {'{year2}'}, {'{code}'}.
                  </span>
                </Label>
              </div>
            </RadioGroup>
            {nameChoice === 'custom' && (
              <div className="flex min-h-10 items-center gap-3 py-2">
                <Label htmlFor="nm-template" className="w-48 shrink-0 text-sm">
                  Шаблон имени
                </Label>
                <Input
                  id="nm-template"
                  className="max-w-xs"
                  placeholder="Урожай {year}"
                  value={draft.name_template ?? ''}
                  onChange={(e) => set({ name_template: e.target.value || null })}
                />
              </div>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* 2. Сколько видно */}
      <Card>
        <CardHeader className="p-4 pb-3">
          <CardTitle className="text-base">Сколько сезонов видно в списках</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <Stack gap={2}>
            <p className="text-sm text-muted-foreground">
              Чтобы выпадашки не разрастались за годы работы. Остальные сезоны не
              удаляются — уходят из рабочих списков.
            </p>
            <div className="flex min-h-10 items-center gap-3 py-2">
              <Label htmlFor="vis-past" className="w-48 shrink-0 text-sm">
                Прошлых сезонов
              </Label>
              <Input
                id="vis-past"
                type="number"
                min={0}
                max={50}
                className="w-24"
                value={draft.visible_past_years ?? 5}
                onChange={(e) =>
                  set({ visible_past_years: Math.max(0, Math.min(50, Number(e.target.value) || 0)) })
                }
              />
            </div>
            <div className="flex min-h-10 items-center gap-3 py-2">
              <Label htmlFor="vis-future" className="w-48 shrink-0 text-sm">
                Будущих сезонов
              </Label>
              <Input
                id="vis-future"
                type="number"
                min={0}
                max={50}
                className="w-24"
                value={draft.visible_future_years ?? 2}
                onChange={(e) =>
                  set({
                    visible_future_years: Math.max(0, Math.min(50, Number(e.target.value) || 0)),
                  })
                }
              />
            </div>
          </Stack>
        </CardContent>
      </Card>

      {/*
        Блок «Когда можно открыть планирование сезона» убран — Р-5 UAT founder
        05.08 («не заказывался»), AK-790 re-land #670. Колонки
        `planning_opens/closes_offset_days` остаются в SeasonConfig и в API,
        backend не трогаем — просто перестаём показывать UI. Понадобится
        вернуть — вернуть отсюда (git log по этому файлу).
      */}

      {/* 4. Границы сезона */}
      <Card>
        <CardHeader className="p-4 pb-3">
          <CardTitle className="text-base">Границы сезона</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <Stack gap={3}>
            <RadioGroup
              value={draft.boundary_type ?? 'calendar'}
              onValueChange={(v) =>
                set({ boundary_type: v as SeasonConfig['boundary_type'] })
              }
            >
              <div className="flex min-h-10 items-center gap-3 py-2">
                <RadioGroupItem value="calendar" id="bd-cal" />
                <Label htmlFor="bd-cal" className="flex-1 text-sm font-normal">
                  <span className="font-medium">Календарный год.</span>{' '}
                  <span className="text-muted-foreground">1 января — 31 декабря.</span>
                </Label>
              </div>
              <div className="flex min-h-10 items-center gap-3 py-2">
                <RadioGroupItem value="agro_year" id="bd-agro" />
                <Label htmlFor="bd-agro" className="flex-1 text-sm font-normal">
                  <span className="font-medium">Агрогод.</span>{' '}
                  <span className="text-muted-foreground">
                    С августа по июль: осенний сев и уборка следующего лета — один сезон.
                    Так считает большинство хозяйств с озимыми.
                  </span>
                </Label>
              </div>
              <div className="flex min-h-10 items-center gap-3 py-2">
                <RadioGroupItem value="custom" id="bd-custom" />
                <Label htmlFor="bd-custom" className="flex-1 text-sm font-normal">
                  <span className="font-medium">Свои даты.</span>{' '}
                  <span className="text-muted-foreground">Начало сезона — вручную.</span>
                </Label>
              </div>
            </RadioGroup>
            {draft.boundary_type === 'custom' && (
              <div className="flex min-h-10 flex-wrap items-center gap-3 py-2">
                <Label className="w-48 shrink-0 text-sm">Старт сезона (день / месяц)</Label>
                <Input
                  type="number"
                  min={1}
                  max={31}
                  className="w-20"
                  aria-label="День старта"
                  value={draft.custom_start_day ?? ''}
                  onChange={(e) => set({ custom_start_day: numOrNull(e.target.value) })}
                />
                <Input
                  type="number"
                  min={1}
                  max={12}
                  className="w-20"
                  aria-label="Месяц старта"
                  value={draft.custom_start_month ?? ''}
                  onChange={(e) => set({ custom_start_month: numOrNull(e.target.value) })}
                />
              </div>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Готовность раздела + Сохранить */}
      <Card>
        <CardHeader className="p-4 pb-3">
          <CardTitle className="text-base">Готовность раздела</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <Stack gap={3}>
            {cfg.is_configured ? (
              <div className="flex min-h-10 flex-wrap items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm">
                <CalendarCheck2 className="h-4 w-4 text-primary" />
                <span className="text-foreground">
                  Настройка сохранена — раздел «Планирование сезона» открыт
                </span>
                <Button asChild variant="ghost" size="sm" className="ml-auto">
                  {/*
                    Пробрасываем companyId, если сюда попали через выбор
                    компании (409 `company_ambiguous`). Иначе `/planning`
                    спросит компанию заново — тот же дефект «второй экран
                    для одного действия», за который head-web дал P1-2.
                  */}
                  <Link
                    to="/planning"
                    search={companyId ? { company_id: companyId } : {}}
                  >
                    Перейти в «Планирование сезона»
                  </Link>
                </Button>
              </div>
            ) : (
              <div className="flex min-h-10 flex-wrap items-center gap-2 rounded-lg border border-border bg-muted px-4 py-2 text-sm text-muted-foreground">
                <Lock className="h-4 w-4" />
                <span>
                  Раздел «Планирование сезона» закрыт — откроется после первого
                  сохранения настройки.
                </span>
              </div>
            )}

            {/*
              Итог сохранения — контракт AK-539 (#664):
                null   — правило не меняли, блок скрыт;
                0      — правило поменяли, но подписи совпали → говорим прямо;
                N > 0  — реально пересчитано N сезонов, ведём в /planning.
              Считает БД-триггер, не UI — здесь только рендер числа.
            */}
            {save.isSuccess && save.data?.seasons_recalculated != null && (
              <RecalcSummary
                count={save.data.seasons_recalculated}
                companyId={companyId}
              />
            )}

            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={submit} disabled={save.isPending}>
                {save.isPending ? 'Сохраняем…' : 'Сохранить настройки'}
              </Button>
              {save.isSuccess && (
                <Badge variant="primary">Сохранено</Badge>
              )}
              {save.isError && (
                <span className="text-sm text-destructive">
                  {(save.error as Error)?.message || 'Не удалось сохранить'}
                </span>
              )}
              <span className="text-xs text-muted-foreground">
                Изменение имени и границ сезона не пересчитывает уже утверждённые
                программы.
              </span>
            </div>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}

/**
 * Итог сохранения настройки — сколько сезонов пересчитал БД-триггер
 * (AK-541b, контракт #664). Три ветки в одном месте, чистый рендер числа.
 * `companyId` пробрасывается в ссылку «Посмотреть новые границы» ровно так же,
 * как в основной ссылке «Перейти в Планирование сезона» — если юзер выбрал
 * компанию в холдинге, /planning не должен спросить её второй раз (P1-2/P1-4).
 */
function RecalcSummary({ count, companyId }: { count: number; companyId: string | null }) {
  if (count === 0) {
    // Формулировка от head-web: первым словом ответ на главный вопрос
    // «сохранилось?», причина после. Прежний вариант («правило календаря
    // обновлено, сезоны трогать не пришлось») отвечал на второй вопрос раньше
    // первого — юзер в этот момент ещё не уверен, что вообще что-то записалось.
    return (
      <div className="flex min-h-10 flex-wrap items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm">
        <CalendarClock className="h-4 w-4 text-muted-foreground" />
        <span className="text-foreground font-medium">Настройка сохранена.</span>
        <span className="text-muted-foreground">
          Границы и названия сезонов не изменились — новое правило дало те же даты.
        </span>
      </div>
    );
  }
  // Тот же словарь: «названия», а не «подписи» — в форме поле называется
  // «Как называются сезоны», значит и в отчёте о результате — «названия».
  return (
    <div className="flex min-h-10 flex-wrap items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm">
      <CalendarClock className="h-4 w-4 text-primary" />
      <span className="text-foreground">
        Пересчитано сезонов: <span className="font-semibold tabular-nums">{count}</span>
      </span>
      <span className="text-muted-foreground">
        — у {pluralSeasonsGen(count)} обновились границы или названия.
      </span>
      <Button asChild variant="ghost" size="sm" className="ml-auto">
        <Link
          to="/planning"
          search={companyId ? { company_id: companyId } : {}}
        >
          Посмотреть новые границы
        </Link>
      </Button>
    </div>
  );
}

/** «1 сезон / 2 сезона / 5 сезонов» — русская плюрализация в именительном. */
export function pluralSeasons(n: number): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs >= 11 && abs <= 14) return `${n} сезонов`;
  if (last === 1) return `${n} сезон`;
  if (last >= 2 && last <= 4) return `${n} сезона`;
  return `${n} сезонов`;
}

/**
 * Родительный падеж «у 1 сезона / 2 сезонов / 5 сезонов» — нужен для оборота
 * «у N сезонов обновились границы или названия» (head-web, финальная
 * формулировка). Правило склонения то же (11-14 исключение), только форма
 * единственного числа меняется с «сезон» на «сезона», а форма мн.ч. в
 * родительном совпадает с формой «5+» — «сезонов». Отдельная функция, а не
 * второй параметр, чтобы вызов на месте читался без магии.
 */
export function pluralSeasonsGen(n: number): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs >= 11 && abs <= 14) return `${n} сезонов`;
  if (last === 1) return `${n} сезона`;
  if (last >= 2 && last <= 4) return `${n} сезонов`;
  return `${n} сезонов`;
}
